import { useQuery } from "@tanstack/react-query";
import { History } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { getHistory, type HistoryItem } from "@/api/admin";
import { Spinner } from "@/components/Spinner";
import { StatusBadge } from "@/components/StatusBadge";
import { formatDateTime } from "@/lib/formatDate";
import { RightPanelDisclosure } from "./RightPanelDisclosure";

export function HistoryDisclosure({
  tabId,
  grievanceId,
  applicationId,
  mode = "real",
  scrollWhenOpen = false,
  openMaxHeightClass,
}: {
  tabId: string;
  grievanceId: number;
  applicationId: string;
  mode?: "real" | "test";
  scrollWhenOpen?: boolean;
  openMaxHeightClass?: string;
}) {
  const nav = useNavigate();

  const gotoTicket = (h: HistoryItem) => {
    if (h.is_test) {
      nav(`/admin/tabs/${tabId}/test-tickets/${h.grievance_id}`);
    } else {
      nav(`/admin/tickets/${h.grievance_id}`);
    }
  };

  return (
    <RightPanelDisclosure
      title="View Past Tickets for this Student"
      icon={<History size={14} />}
      scrollWhenOpen={scrollWhenOpen}
      openMaxHeightClass={openMaxHeightClass}
    >
      <HistoryBody
        tabId={tabId}
        grievanceId={grievanceId}
        applicationId={applicationId}
        mode={mode}
        onGoto={gotoTicket}
      />
    </RightPanelDisclosure>
  );
}

function HistoryBody({
  tabId,
  grievanceId,
  applicationId,
  mode,
  onGoto,
}: {
  tabId: string;
  grievanceId: number;
  applicationId: string;
  mode: "real" | "test";
  onGoto: (h: HistoryItem) => void;
}) {
  const q = useQuery({
    queryKey: ["history", tabId, grievanceId, applicationId, mode],
    queryFn: () => getHistory(tabId, grievanceId, applicationId, mode === "test"),
  });

  if (q.isLoading) return <div className="p-5"><Spinner label="Loading history…" /></div>;
  if (q.isError) return <div className="p-5 alert-danger">{(q.error as Error)?.message}</div>;
  const items = q.data?.items ?? [];
  if (items.length === 0) {
    return (
      <div className="p-5 alert-info">No other past tickets found for this student.</div>
    );
  }
  return (
    <div className="p-4 space-y-3">
      <p className="text-xs text-slate-500 dark:text-slate-400">
        Found {items.length} other ticket(s) for{" "}
        <span className="font-mono font-semibold text-brand-appId">{applicationId}</span>.
      </p>
      <ul className="space-y-3">
        {items.map((h) => (
          <li
            key={`${h.is_test ? "t" : "r"}-${h.grievance_id}`}
            className="rounded-md border border-slate-200 dark:border-surface-border p-3 bg-slate-50 dark:bg-surface-raised/40"
          >
            <div className="flex items-center justify-between gap-3 mb-1">
              <span className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500 dark:text-slate-400">
                Ticket #{h.grievance_id}
                {h.is_test && (
                  <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
                    Test
                  </span>
                )}
              </span>
              <StatusBadge status={h.status} />
            </div>
            {h.sent_date_time && (
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-2">
                Raised on{" "}
                <span className="font-mono">{formatDateTime(h.sent_date_time)}</span>
              </p>
            )}
            <p className="text-sm whitespace-pre-wrap break-words mb-2">{h.grievance}</p>
            {h.status === "Acknowledged" && h.replied_grievance ? (
              <div className="alert-success text-xs whitespace-pre-wrap">
                <span className="font-semibold">Reply:</span> {h.replied_grievance}
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <div className="alert-warning text-xs flex-1">This ticket is currently Pending.</div>
                <button
                  type="button"
                  className="btn-secondary text-xs"
                  onClick={() => onGoto(h)}
                >
                  Go to this ticket
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
